"use client";

import { FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { HomeBanner } from "@components/ui-kit/organisms/banners";
import { useScrollableViewportContext } from "@components/ui-kit/organisms/scrollable-viewport/scrollable-viewport-context";
import { getHomeBreakpointKey, HomeGlobalSettings, HomeItem, HomeModelItem, isHideOnMoveBanner, isHomeBannerItem, isHomeModelItem, resolveHomeCenteredScrollPosition, resolveHomeInitialScrollPosition, resolveHomeItemLayoutBounds, resolveHomeOriginScrollPosition, resolveHomePriorityItem } from "@entities/home/helpers";
import useDataLayerAnalytics from "@hooks/use-analytics";
import HomeItemBlock from "@views/home/components/home-item";
import styles from "@views/home/home.module.scss";
import { useHomeItemDismiss } from "@views/home/hooks/use-home-item-dismiss";
type Props = {
  initialItems?: HomeItem[];
  homeGlobalSettings?: HomeGlobalSettings;
  isMenuOpen?: boolean;
};
type KeyboardState = {
  itemId: number | null;
  originIndex: number;
};
const HomePageView: FunctionComponent<Props> = ({
  initialItems = [],
  homeGlobalSettings = {
    defaultModelId: null,
    centerOffset: []
  },
  isMenuOpen = false
}) => {
  const POPUP_EXIT_MS = 220;
  const homeWrapperRef = useRef<HTMLDivElement>(null);
  const prevScrollRef = useRef<{
    left: number;
    top: number;
  } | null>(null);
  const popupExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didInitScrollRef = useRef(false);
  const [cardState, setCardState] = useState<{
    activeItemId: number | null;
    activeOriginIndex: number;
    position: {
      x: number;
      y: number;
    } | null;
    isOpen: boolean;
  }>({
    activeItemId: null,
    activeOriginIndex: 0,
    position: null,
    isOpen: false
  });
  const [keyboardState, setKeyboardState] = useState<KeyboardState>({
    itemId: null,
    originIndex: 0
  });
  const {
    setDataLayerAllProductMain
  } = useDataLayerAnalytics();
  const {
    size,
    scrollTo
  } = useScrollableViewportContext();
  const breakpoint = getHomeBreakpointKey(size.viewportWidth);
  const worldOffset = useMemo(() => {
    const wrapper = homeWrapperRef.current;
    if (!wrapper) {
      return {
        x: size.viewportWidth / 2,
        y: size.viewportHeight / 2
      };
    }
    const computedStyles = window.getComputedStyle(wrapper);
    const paddingLeft = parseFloat(computedStyles.paddingLeft) || 0;
    const paddingTop = parseFloat(computedStyles.paddingTop) || 0;
    return {
      x: paddingLeft,
      y: paddingTop
    };
  }, [size.viewportWidth, size.viewportHeight]);
  const hideOnMoveIds = useMemo(() => initialItems.filter(isHideOnMoveBanner).map(item => item.id), [initialItems]);
  const navigableItems = useMemo(() => initialItems.filter(isHomeModelItem).filter(item_0 => (item_0.productInfos?.length ?? (item_0.productInfo ? 1 : 0)) > 0), [initialItems]);
  const priorityItemId = useMemo(() => resolveHomePriorityItem(initialItems, homeGlobalSettings)?.id ?? null, [initialItems, homeGlobalSettings]);
  const centerZoomedItemId = useMemo(() => {
    if (size.viewportWidth <= 0 || size.viewportHeight <= 0) return null;
    if (initialItems.length === 0) return null;
    const bp = getHomeBreakpointKey(size.viewportWidth);
    const worldCenterX = size.viewportWidth / 2 - worldOffset.x + size.scrollLeft;
    const worldCenterY = size.viewportHeight / 2 - worldOffset.y + size.scrollTop;
    const worldViewportLeft = size.scrollLeft - worldOffset.x;
    const worldViewportTop = size.scrollTop - worldOffset.y;
    const worldViewportRight = worldViewportLeft + size.viewportWidth;
    const worldViewportBottom = worldViewportTop + size.viewportHeight;
    type Candidate = {
      id: number;
      containsCenter: boolean;
      visibleArea: number;
      itemArea: number;
    };
    const candidates: Candidate[] = initialItems.filter(item_1 => isHomeModelItem(item_1) || isHideOnMoveBanner(item_1)).flatMap(item_2 => {
      const bounds = resolveHomeItemLayoutBounds(item_2, bp);
      const width = bounds.right - bounds.left;
      const height = bounds.bottom - bounds.top;
      if (width <= 0 || height <= 0) return [];
      const containsCenter = worldCenterX >= bounds.left && worldCenterX <= bounds.right && worldCenterY >= bounds.top && worldCenterY <= bounds.bottom;
      const visibleWidth = Math.max(0, Math.min(bounds.right, worldViewportRight) - Math.max(bounds.left, worldViewportLeft));
      const visibleHeight = Math.max(0, Math.min(bounds.bottom, worldViewportBottom) - Math.max(bounds.top, worldViewportTop));
      const visibleArea = visibleWidth * visibleHeight;
      return [{
        id: item_2.id,
        containsCenter,
        visibleArea,
        itemArea: width * height
      }];
    });
    const centered = candidates.filter(item_3 => item_3.containsCenter);
    if (centered.length > 0) {
      return centered.sort((a, b) => a.itemArea - b.itemArea)[0].id;
    }
    return candidates.sort((a_0, b_0) => b_0.visibleArea - a_0.visibleArea || a_0.itemArea - b_0.itemArea)[0]?.id ?? null;
  }, [initialItems, size.scrollLeft, size.scrollTop, size.viewportWidth, size.viewportHeight, worldOffset]);
  const focusedHideOnMoveId = centerZoomedItemId != null && hideOnMoveIds.includes(centerZoomedItemId) ? centerZoomedItemId : null;
  const {
    isItemDismissed
  } = useHomeItemDismiss({
    hideOnMoveIds,
    focusedHideOnMoveId
  });
  const handleShowCard = useCallback((itemId: number, x: number, y: number, originIndex: number) => {
    if (popupExitTimerRef.current) {
      clearTimeout(popupExitTimerRef.current);
      popupExitTimerRef.current = null;
    }
    setKeyboardState({
      itemId,
      originIndex
    });
    setCardState({
      activeItemId: itemId,
      activeOriginIndex: originIndex,
      position: {
        x,
        y
      },
      isOpen: true
    });
  }, []);
  const handleCloseCard = useCallback(() => {
    setCardState(prev => {
      if (prev.activeItemId == null) return prev;
      return {
        ...prev,
        isOpen: false
      };
    });
  }, []);
  const scrollToItem = useCallback((item_4: HomeItem, behavior: ScrollBehavior = "smooth") => {
    if (size.viewportWidth <= 0 || size.viewportHeight <= 0) return;
    const {
      scrollLeft,
      scrollTop
    } = resolveHomeCenteredScrollPosition({
      viewportWidth: size.viewportWidth,
      viewportHeight: size.viewportHeight,
      item: item_4,
      worldOffset,
      globalSettings: homeGlobalSettings
    });
    scrollTo(scrollLeft, scrollTop, behavior);
  }, [homeGlobalSettings, scrollTo, size.viewportHeight, size.viewportWidth, worldOffset]);
  const scrollToOrigin = useCallback((item_5: HomeModelItem, originIndex_0: number) => {
    if (size.viewportWidth <= 0 || size.viewportHeight <= 0) return;
    const position = resolveHomeOriginScrollPosition({
      viewportWidth: size.viewportWidth,
      viewportHeight: size.viewportHeight,
      item: item_5,
      originIndex: originIndex_0,
      worldOffset
    });
    if (!position) return;
    scrollTo(position.scrollLeft, position.scrollTop, "smooth");
  }, [scrollTo, size.viewportHeight, size.viewportWidth, worldOffset]);
  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      setDataLayerAllProductMain(initialItems.filter(isHomeModelItem));
    }
  }, [initialItems, setDataLayerAllProductMain]);
  useEffect(() => {
    if (size.viewportWidth <= 0 || size.viewportHeight <= 0) return;
    const prev_0 = prevScrollRef.current;
    const current = {
      left: size.scrollLeft,
      top: size.scrollTop
    };
    prevScrollRef.current = current;
    if (!prev_0) return;
    if (prev_0.left === current.left && prev_0.top === current.top) return;
    if (navigableItems.length > 0 && centerZoomedItemId != null) {
      setKeyboardState(prevState => {
        if (prevState.itemId === centerZoomedItemId) {
          return prevState;
        }
        return {
          itemId: centerZoomedItemId,
          originIndex: 0
        };
      });
    }
    handleCloseCard();
  }, [handleCloseCard, size.scrollLeft, size.scrollTop, size.viewportWidth, size.viewportHeight, centerZoomedItemId, navigableItems]);
  useLayoutEffect(() => {
    if (didInitScrollRef.current) return;
    if (size.viewportWidth <= 0 || size.viewportHeight <= 0) return;
    if (initialItems.length === 0) return;

    // Sync до VT new-snapshot: иначе в transition виден (0,0), а скролл — после анимации.
    const {
      scrollLeft: scrollLeft_0,
      scrollTop: scrollTop_0
    } = resolveHomeInitialScrollPosition({
      viewportWidth: size.viewportWidth,
      viewportHeight: size.viewportHeight,
      initialItems,
      worldOffset,
      globalSettings: homeGlobalSettings
    });
    scrollTo(scrollLeft_0, scrollTop_0, "auto");
    didInitScrollRef.current = true;
  }, [homeGlobalSettings, initialItems, scrollTo, size.viewportHeight, size.viewportWidth, worldOffset]);
  useEffect(() => {
    const onRecenter = () => {
      if (size.viewportWidth <= 0 || size.viewportHeight <= 0) return;
      if (initialItems.length === 0) return;
      const {
        scrollLeft: scrollLeft_1,
        scrollTop: scrollTop_1
      } = resolveHomeInitialScrollPosition({
        viewportWidth: size.viewportWidth,
        viewportHeight: size.viewportHeight,
        initialItems,
        worldOffset,
        globalSettings: homeGlobalSettings
      });
      scrollTo(scrollLeft_1, scrollTop_1, "smooth");
    };
    window.addEventListener("home:recenter", onRecenter);
    return () => window.removeEventListener("home:recenter", onRecenter);
  }, [homeGlobalSettings, initialItems, scrollTo, size.viewportHeight, size.viewportWidth, worldOffset]);
  useEffect(() => {
    if (cardState.isOpen || cardState.activeItemId == null) return;
    popupExitTimerRef.current = setTimeout(() => {
      setCardState({
        activeItemId: null,
        activeOriginIndex: 0,
        position: null,
        isOpen: false
      });
      popupExitTimerRef.current = null;
    }, POPUP_EXIT_MS);
    return () => {
      if (popupExitTimerRef.current) {
        clearTimeout(popupExitTimerRef.current);
        popupExitTimerRef.current = null;
      }
    };
  }, [POPUP_EXIT_MS, cardState.activeItemId, cardState.isOpen]);
  useEffect(() => {
    handleCloseCard();
  }, [isMenuOpen, handleCloseCard]);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key !== "Escape") return;
      if (!cardState.isOpen) return;
      e.preventDefault();
      handleCloseCard();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [cardState.isOpen, handleCloseCard]);
  useEffect(() => {
    const onKeyDown_0 = (e_0: KeyboardEvent) => {
      if (e_0.defaultPrevented) return;
      if (e_0.metaKey || e_0.ctrlKey || e_0.altKey) return;
      if (e_0.key !== "ArrowLeft" && e_0.key !== "ArrowRight") return;
      if (e_0.target instanceof HTMLElement) {
        const tag = e_0.target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e_0.target.isContentEditable) {
          return;
        }
      }
      if (navigableItems.length === 0) return;
      e_0.preventDefault();
      const currentItemId = keyboardState.itemId ?? centerZoomedItemId ?? navigableItems[0]?.id ?? null;
      if (currentItemId == null) return;
      const currentIndex = navigableItems.findIndex(candidate => candidate.id === currentItemId);
      const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex = e_0.key === "ArrowRight" ? (safeCurrentIndex + 1) % navigableItems.length : (safeCurrentIndex - 1 + navigableItems.length) % navigableItems.length;
      const nextItem = navigableItems[nextIndex];
      if (!nextItem) return;
      handleCloseCard();
      scrollToItem(nextItem);
      setKeyboardState({
        itemId: nextItem.id,
        originIndex: 0
      });
    };
    window.addEventListener("keydown", onKeyDown_0);
    return () => window.removeEventListener("keydown", onKeyDown_0);
  }, [centerZoomedItemId, handleCloseCard, keyboardState.itemId, navigableItems, scrollToItem]);
  return <div ref={homeWrapperRef} className={clsx(styles.homeWrapper, "background")}>
      <div className={styles.homeWorld}>
        <div role="presentation" className={styles.homeBackdrop} onClick={handleCloseCard} style={{
        pointerEvents: !cardState.isOpen ? "none" : undefined
      }} />
        {initialItems.map(homeItem => isHomeBannerItem(homeItem) ? <HomeBanner key={homeItem.id} item={homeItem} breakpoint={breakpoint} isDismissed={homeItem.hideOnMove && isItemDismissed(homeItem.id)} /> : <HomeItemBlock key={homeItem.id} item={homeItem} isPriorityImage={homeItem.id === priorityItemId} breakpoint={breakpoint} isCardOpen={cardState.activeItemId === homeItem.id && cardState.isOpen} isCardVisible={cardState.activeItemId === homeItem.id && cardState.position !== null} activeOriginIndex={cardState.activeOriginIndex} cardPosition={cardState.activeItemId === homeItem.id ? cardState.position : null} isCenterZoomed={centerZoomedItemId === homeItem.id} isKeyboardActiveItem={keyboardState.itemId === homeItem.id} keyboardOriginIndex={keyboardState.originIndex} onKeyboardNavigateItem={(item_6, originIndex_1) => {
        scrollToOrigin(item_6, originIndex_1);
      }} onKeyboardStateChange={(itemId_0, originIndex_2) => {
        setKeyboardState({
          itemId: itemId_0,
          originIndex: originIndex_2
        });
      }} onShowCard={handleShowCard} onCloseCard={handleCloseCard} />)}
      </div>
    </div>;
};
export default HomePageView;